import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { IEvent, CALENDAR_KEYS } from '@/types/admin/events';
import { formatEventType, generateDayEventList } from '@/hooks/admin/useEvents';
import { LazyImage } from '@/components/common/admin/mobile/LazyImage';
import { EventDetailsModal } from './EventDetailsModal';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { SVG_COMPONENTS } from '@/assets/admin';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

interface CalendarDayCellColors {
  primaryText: string;
  subtleText: string;
  todayBg: string;
  todayAccent: string;
  todayText: string;
  hasEventsBg: string;
  defaultEventDot: string;
}

interface CalendarDayCellProps {
  date: Date;
  events: IEvent[];
  isToday: boolean;
  colors: CalendarDayCellColors;
  onSelect: (event: IEvent) => void;
}

const CalendarDayCell: React.FC<CalendarDayCellProps> = React.memo(
  ({ date, events, isToday, colors, onSelect }) => {
    const hasEvents = events.length > 0;
    const primaryEvent = hasEvents ? events[0] : null;
    const primaryDotColor = useMemo(() => {
      if (!primaryEvent) return colors.defaultEventDot;
      const eventType = formatEventType(primaryEvent);
      const eventKey = CALENDAR_KEYS.find((k) => k.title === eventType);
      return eventKey?.color || colors.defaultEventDot;
    }, [primaryEvent, colors.defaultEventDot]);

    const handlePress = useCallback(() => {
      if (primaryEvent) {
        onSelect(primaryEvent);
      }
    }, [primaryEvent, onSelect]);

    const cellStyle = isToday
      ? [styles.calendarDay, { backgroundColor: colors.todayBg }]
      : hasEvents
        ? [styles.calendarDay, { backgroundColor: colors.hasEventsBg }]
        : styles.calendarDay;

    return (
      <TouchableOpacity
        style={cellStyle}
        onPress={handlePress}
        activeOpacity={hasEvents ? 0.7 : 1}
        disabled={!hasEvents}
      >
        <Text
          scale="micro"
          style={[
            styles.dayNumber,
            { color: isToday ? colors.todayText : colors.primaryText },
            isToday && { fontWeight: '700' },
            !isToday && hasEvents && { fontWeight: '600' },
          ]}
        >
          {date.getDate()}
        </Text>

        {hasEvents && (
          <View style={styles.eventIndicators}>
            <View style={[styles.eventDot, { backgroundColor: primaryDotColor }]} />
            {events.length > 1 && (
              <Text
                scale="micro"
                style={[
                  styles.moreEventsText,
                  { color: isToday ? colors.todayText : colors.subtleText },
                  isToday && styles.moreEventsTextOnToday,
                ]}
              >
                {events.length}
              </Text>
            )}
          </View>
        )}
      </TouchableOpacity>
    );
  },
);
CalendarDayCell.displayName = 'CalendarDayCell';

interface EventsCalendarProps {
  monthlyEvents: IEvent[];
  selectedMonth: Date;
  onMonthChange: (month: Date) => void;
  onEventSelect?: (event: IEvent) => void;
  onEditEvent?: (eventId: number) => void;
  // Desktop-only options: hide internal events list and disable modal behavior
  showEventsList?: boolean;
  modalBehavior?: 'modal' | 'none';
}

export const EventsCalendar: React.FC<EventsCalendarProps> = ({
  monthlyEvents,
  selectedMonth,
  onMonthChange,
  onEventSelect,
  onEditEvent,
  showEventsList = true,
  modalBehavior = 'modal',
}) => {
  const { redesign, semantic } = useAdminColors();
  const [currentDate, setCurrentDate] = useState(selectedMonth);
  const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Keep internal currentDate in sync if parent selectedMonth changes externally
  useEffect(() => {
    if (
      selectedMonth.getFullYear() !== currentDate.getFullYear() ||
      selectedMonth.getMonth() !== currentDate.getMonth()
    ) {
      setCurrentDate(selectedMonth);
    }
  }, [selectedMonth, currentDate]);

  // Generate calendar grid
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    // Create calendar grid
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Calculate number of weeks needed
    const totalCells = startingDayOfWeek + daysInMonth;
    const weeksNeeded = Math.ceil(totalCells / 7);

    return { days, weeksNeeded };
  }, [currentDate]);

  // Get events for each day
  const dayEventPairings = useMemo(() => {
    return generateDayEventList(monthlyEvents);
  }, [monthlyEvents]);

  // Handle event selection
  const handleEventSelect = useCallback(
    (event: IEvent) => {
      if (modalBehavior === 'modal') {
        setSelectedEvent(event);
        setModalVisible(true);
      }
      onEventSelect?.(event);
    },
    [modalBehavior, onEventSelect],
  );

  const cellColors = useMemo<CalendarDayCellColors>(
    () => ({
      primaryText: redesign.text.primary,
      subtleText: redesign.text.subtle,
      todayBg: PRIMITIVE_COLORS.resioBlue,
      todayAccent: PRIMITIVE_COLORS.resioBlue,
      todayText: '#FFFFFF',
      hasEventsBg: redesign.glass.buttonBg,
      defaultEventDot: PRIMITIVE_COLORS.resioBlue,
    }),
    [redesign.text.primary, redesign.text.subtle, redesign.glass.buttonBg],
  );

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedEvent(null);
  };

  const handleEditEvent = (eventId: number) => {
    setModalVisible(false);
    setSelectedEvent(null);
    onEditEvent?.(eventId);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
    onMonthChange(newDate);
  };

  const getEventsForDay = (date: Date | null) => {
    if (!date) return [];
    // Build the key from local Y-M-D to match generateDayEventList's property-local
    // bucket keys. toISOString() would first convert to UTC and drift the cell's date.
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      '0',
    )}-${String(date.getDate()).padStart(2, '0')}`;
    return dayEventPairings[dateKey] || [];
  };

  const renderCalendarDay = (date: Date | null, index: number) => {
    if (!date) {
      return <View key={index} style={styles.emptyDay} />;
    }

    const events = getEventsForDay(date);
    const isToday = date.toDateString() === new Date().toDateString();

    return (
      <CalendarDayCell
        key={index}
        date={date}
        events={events}
        isToday={isToday}
        colors={cellColors}
        onSelect={handleEventSelect}
      />
    );
  };

  const renderEventsList = () => {
    // monthlyEvents is already filtered to the selected property-local month by
    // useEvents, so dayEventPairings only contains keys for that month. Filtering
    // again with new Date(YYYY-MM-DD).getMonth() drops days when UTC-midnight
    // rolls back into the previous month for the device's timezone.
    const sortedDays = Object.keys(dayEventPairings).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );

    if (sortedDays.length === 0) {
      return (
        <View style={styles.noEventsContainer}>
          <Text style={[styles.noEventsText, { color: redesign.text.subtle }]}>
            No events this month
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.eventsList}>
        {sortedDays.map((day) => {
          const events = dayEventPairings[day];
          const dayDate = new Date(day);

          return (
            <View key={day} style={styles.dayEventsContainer}>
              <Text style={[styles.dayHeader, { color: redesign.text.muted }]}>
                {dayDate.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'UTC',
                })}
              </Text>
              {events.map((event) => {
                const eventType = formatEventType(event);
                const eventKey = CALENDAR_KEYS.find((key) => key.title === eventType);

                return (
                  <TouchableOpacity
                    key={event.id}
                    onPress={() => handleEventSelect(event)}
                    activeOpacity={0.7}
                  >
                    <GlassCard
                      padding={12}
                      borderRadius={8}
                      style={[
                        styles.eventItemSpacing,
                        {
                          borderLeftWidth: 3,
                          borderLeftColor: eventKey?.color || redesign.accent.blueBg,
                        },
                      ]}
                    >
                      <View style={styles.eventItemContent}>
                        {/* Event Image */}
                        <View style={styles.eventImageContainer}>
                          {(() => {
                            const firstPhotoUrl = event.photos?.[0]?.photoUrl;
                            if (firstPhotoUrl) {
                              return (
                                <LazyImage
                                  source={{ uri: firstPhotoUrl }}
                                  style={styles.eventImage}
                                  resizeMode="cover"
                                  placeholder={
                                    <View
                                      style={[
                                        styles.eventImagePlaceholder,
                                        { backgroundColor: redesign.glass.buttonBg },
                                      ]}
                                    >
                                      <SVG_COMPONENTS.CALENDAR_EVENT
                                        width={28}
                                        height={28}
                                        color={redesign.text.subtle}
                                      />
                                    </View>
                                  }
                                  errorPlaceholder={
                                    <View
                                      style={[
                                        styles.eventImagePlaceholder,
                                        { backgroundColor: redesign.glass.buttonBg },
                                      ]}
                                    >
                                      <SVG_COMPONENTS.X_CIRCLE
                                        width={24}
                                        height={24}
                                        color={semantic.status.error}
                                      />
                                    </View>
                                  }
                                />
                              );
                            }
                            return (
                              <View
                                style={[
                                  styles.eventImagePlaceholder,
                                  { backgroundColor: redesign.glass.buttonBg },
                                ]}
                              >
                                <SVG_COMPONENTS.CALENDAR_EVENT
                                  width={28}
                                  height={28}
                                  color={redesign.text.subtle}
                                />
                              </View>
                            );
                          })()}
                        </View>

                        {/* Event Details */}
                        <View style={styles.eventDetails}>
                          <View style={styles.eventItemHeader}>
                            <View style={styles.eventItemLeft}>
                              <View
                                style={[
                                  styles.eventStatusDot,
                                  {
                                    backgroundColor:
                                      eventKey?.color || redesign.accent.blueBg,
                                  },
                                ]}
                              />
                              <Text
                                style={[styles.eventName, { color: redesign.text.primary }]}
                                numberOfLines={2}
                              >
                                {event.name}
                              </Text>
                            </View>
                            <Text style={[styles.eventTime, { color: redesign.text.muted }]}>
                              {new Date(event.startTime).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Text>
                          </View>
                          <Text style={[styles.eventRsvps, { color: redesign.text.subtle }]}>
                            {event.n_rsvps} RSVPs
                          </Text>
                        </View>
                      </View>
                    </GlassCard>
                  </TouchableOpacity>
                );
              })}
            </View>
          );
        })}
      </View>
    );
  };

  const Wrapper = showEventsList ? ScrollView : View;
  const wrapperProps: any = showEventsList
    ? {
        showsVerticalScrollIndicator: false,
        contentContainerStyle: styles.scrollContent,
      }
    : {};

  return (
    <Wrapper
      style={showEventsList ? styles.container : styles.containerStatic}
      {...wrapperProps}
    >
      {/* Calendar Header + Grid as one GlassCard */}
      <GlassCard padding={16} borderRadius={20} style={styles.calendarCard}>
        <View style={styles.calendarHeader}>
          <TouchableOpacity
            style={[styles.navButton, { backgroundColor: redesign.glass.buttonBg }]}
            onPress={() => navigateMonth('prev')}
          >
            <Text style={[styles.navButtonText, { color: redesign.text.primary }]}>‹</Text>
          </TouchableOpacity>

          <Text style={[styles.monthYearText, { color: redesign.text.primary }]}>
            {currentDate.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })}
          </Text>

          <TouchableOpacity
            style={[styles.navButton, { backgroundColor: redesign.glass.buttonBg }]}
            onPress={() => navigateMonth('next')}
          >
            <Text style={[styles.navButtonText, { color: redesign.text.primary }]}>›</Text>
          </TouchableOpacity>
        </View>

        {/* Days of week header */}
        <View style={styles.weekHeader}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Text key={day} style={[styles.weekDay, { color: redesign.text.muted }]}>
              {day}
            </Text>
          ))}
        </View>

        {/* Calendar Grid */}
        {/* No fixed box: a flexWrap row of minHeight cells sizes itself, so grown cells push the legend down. */}
        <View style={styles.calendarGrid}>
          {calendarData.days.map((date, index) => renderCalendarDay(date, index))}
        </View>
      </GlassCard>

      {/* Status Legend */}
      <GlassCard padding={12} borderRadius={8} style={styles.legendCard}>
        <View style={styles.legend}>
          {CALENDAR_KEYS.map((key) => (
            <View key={key.title} style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: key.color }]} />
              <Text style={[styles.legendText, { color: redesign.text.muted }]}>
                {key.title}
              </Text>
            </View>
          ))}
        </View>
      </GlassCard>

      {/* Events List */}
      {showEventsList && (
        <GlassCard padding={16} borderRadius={8} style={styles.eventsListCard}>
          <Text style={[styles.eventsListTitle, { color: redesign.text.primary }]}>
            {currentDate.toLocaleDateString('en-US', { month: 'long' })} Events
          </Text>
          {renderEventsList()}
        </GlassCard>
      )}

      {/* Event Details Modal */}
      {modalBehavior === 'modal' && (
        <AdminErrorBoundary context="EventDetailsModal">
          <EventDetailsModal
            visible={modalVisible}
            event={selectedEvent}
            onClose={handleCloseModal}
            onEdit={handleEditEvent}
          />
        </AdminErrorBoundary>
      )}
    </Wrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerStatic: {
    // Desktop: natural height so sibling event-selector can flex into the
    // remaining space instead of competing with a flex:1 ScrollView.
  },
  scrollContent: {
    // Remove flexGrow to prevent unnecessary expansion
  },
  calendarCard: {
    marginBottom: 14,
  },
  calendarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    marginBottom: 12,
  },
  navButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
  },
  navButtonText: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  monthYearText: {
    fontSize: 18,
    fontWeight: '600',
  },
  weekHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    marginBottom: 4,
  },
  weekDay: {
    flex: 1,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '500',
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  calendarDay: {
    width: '14.28%', // 100% / 7 days
    minHeight: 50,
    padding: 4,
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderRadius: 4,
  },
  emptyDay: {
    width: '14.28%',
    minHeight: 50,
  },
  dayNumber: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
  eventIndicators: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  eventDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginHorizontal: 2,
    marginVertical: 1,
  },
  moreEventsText: {
    fontSize: 9,
    marginLeft: 2,
  },
  moreEventsTextOnToday: {
    fontWeight: '700',
  },
  legendCard: {
    marginBottom: 8,
  },
  legend: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  legendText: {
    fontSize: 12,
  },
  eventsListCard: {
    // padding handled by GlassCard
  },
  eventsListTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  eventsList: {
    // Remove flex: 1 since parent ScrollView handles scrolling
  },
  noEventsContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  noEventsText: {
    fontSize: 14,
    textAlign: 'center',
  },
  dayEventsContainer: {
    marginBottom: 20,
  },
  dayHeader: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 10,
  },
  eventItemSpacing: {
    marginBottom: 8,
  },
  eventItemContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  eventImageContainer: {
    marginRight: 12,
  },
  eventImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
  },
  eventImagePlaceholder: {
    width: 50,
    height: 50,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  eventDetails: {
    flex: 1,
  },
  eventItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  eventItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  eventStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  eventName: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
  },
  eventTime: {
    fontSize: 12,
  },
  eventRsvps: {
    fontSize: 12,
  },
});
